import NavigationBarIconButton from '@/components/Common/DesignSystem/NavigationBar/IconButton/NavigationBarIconButton';
import NavigationBar from '@/components/Common/DesignSystem/NavigationBar/NavigationBar';
import PageTitle from '@/components/Common/PageTitle/PageTitle';
import SunnyLayout from '@/components/Common/SunnyLayout/SunnyLayout';
import SunnyNav from '@/components/Common/SunnyNav/SunnyNav';
import { SunnyFC } from '@/components/Common/SunnyNext';
import { withSunnyPage } from '@/components/Common/SunnyPage';
import TrainSHomeBody from '@/components/Train/SHome/TrainSHomeBody';
import { ITrainSHomeTitle } from '@/models/Transportation/interfaces/payloads/ITransportationCommon';
import { ITrainWidgetNotificationPayload } from '@/models/Train/interfaces/ITrainWidgetNotificationPayload';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { generatePageMeta } from '@/services/SEOService/metaGenerator';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import TrainSHomeStore, { TrainSHomeStoreCtx } from '@/stores/TrainStore/TrainSHomeStore';
import { forceBackFromExternal } from '@/utils';
import { getRtbMemberId, pushRtbEvents } from '@/utils/rtb';
import { useScrollStore } from '@/utils/hooks';
import React, { useContext, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { getYaNative } from '@yanolja-org/ya-native';

import { ISHomeBenefitBannerPayload } from '@/models/SHome/interfaces/ISHomeBenefitBannerPayload';
import { nullish } from '@/utils/yambda';
import { getPlatformOnServer } from '@/utils/PlatformUtils';
import { identity, prop } from 'ramda';
import TrainActions from '@/models/Train/TrainActions';
import TrainRequestBuilder from '@/models/Train/TrainRequestBuilder';
import { EN_DEUS_NOTIFICATION_TARGET } from '@@achasan/widget/notification/enum/EN_DEUS_NOTIFICATION_TARGET';

import styles from './index.module.scss';
import NavigationBarHeadingFullCenter from '@/components/Common/DesignSystem/NavigationBar/Heading/NavigationBarHeadingFullCenter';

export interface ITrainSHomeQuery {
  [key: string]: unknown;
  departureStationCode?: string;
  arrivalStationCode?: string;
  departureDate?: string;
  returnDate?: string;
  adult?: string;
  child?: string;
  tripType?: string;
}

export interface ITrainSHomeProps {
  title: ITrainSHomeTitle;
  benefitBanner: ISHomeBenefitBannerPayload | null;
  widgetNotification: ITrainWidgetNotificationPayload | null;
  query: ITrainSHomeQuery;
}

const TrainSHome: SunnyFC<ITrainSHomeProps> = (props) => {
  const store = useRef(new TrainSHomeStore(props)).current;
  const globalStore = useContext(GlobalStoreCtx);

  useScrollStore();

  /** Query parameter로 넘겨준 값들 검사 후 sessionStore에 저장 */
  useEffect(() => {
    (async (): Promise<void> => {
      await store.fetchStations(); // 역 이름 validation 을 위한 pre-fetch
      store.validateQueryParams(); // url query validation 후 localStorage 에 저장
      store.initSearchData(); // localStorage 의 값으로 검색 조건 초기화
    })();
  }, []);

  useEffect(() => {
    pushRtbEvents({ eventType: 'category', category_name: 'd_tr' }, { eventType: 'uid', id: getRtbMemberId() });
  }, []);

  const goBackHome = (): void => {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.dismiss();
    } else {
      forceBackFromExternal('/');
    }
  };

  const goToSearch = (): void => {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: 'yanoljamotel://search?tab=place' });
    } else {
      window.location.href = '/search';
    }
  };

  return (
    <TrainSHomeStoreCtx.Provider value={store}>
      <SunnyLayout className={styles.layout}>
        {{
          header: (
            <SunnyNav
              legacyNav={
                <PageTitle
                  className={styles.legacyPageTitle}
                  hasBottomBorder={true}
                  left="back"
                  right={{
                    search: () => (location.href = '/search'),
                    cart: globalStore.onClickNavCartBtn,
                  }}
                  onClickLeft={goBackHome}
                  cartCount={globalStore.CartCount}
                >
                  {store.title.title}
                </PageTitle>
              }
            >
              <>
                <NavigationBar>
                  {{
                    left: <NavigationBarIconButton as="button" type="icon_arrowback" onClick={goBackHome} />,
                    center: <NavigationBarHeadingFullCenter>{store.title.title}</NavigationBarHeadingFullCenter>,
                    right: (
                      <>
                        <NavigationBarIconButton as="link" type="icon_search" onClick={goToSearch} />
                        <NavigationBarIconButton
                          as="link"
                          type="icon_cart"
                          count={globalStore.CartCount}
                          onClick={globalStore.onClickNavCartBtn}
                        />
                      </>
                    ),
                  }}
                </NavigationBar>
                <NavigationBar.Body>
                  <NavigationBar.Shadow />
                </NavigationBar.Body>
              </>
            </SunnyNav>
          ),
          body: <TrainSHomeBody />,
        }}
      </SunnyLayout>
    </TrainSHomeStoreCtx.Provider>
  );
};

TrainSHome.getInitialProps = async ({ req, query }) => {
  const title = {
    title: '기차',
    description: '야놀자와 인터파크의 새로운 이름, NOLㅣ기차/KTX 승차권을 NOL에서 할인 혜택으로 예약해 보세요.',
    sellerURL: req.requestedUrl + '/vendor',
  };

  /** Deus를 통해 관리되는 값들. Joy api 사용 */
  const actions = new TrainActions(TrainRequestBuilder, req);

  const platform = getPlatformOnServer(req).toHeader();
  const widgetNotification = (
    await actions.widgetNotification({
      headers: {
        platform,
      },
      query: {
        target: EN_DEUS_NOTIFICATION_TARGET.SHOME_KTX,
      },
    })
  )
    .map(prop('data'))
    .caseOf({
      left: nullish,
      right: identity,
    });

  return {
    title,
    benefitBanner: null,
    widgetNotification,
    query,
    headProps: generatePageMeta({
      pageUrl: req.requestedUrl,
      pageName: EN_PAGE_NAME.PAGE__TRAIN__SHOME,
      options: {
        title: '기차/KTX 승차권 최저가 할인 예매 | NOL(야놀자)',
        description: title.description,
      },
    }),
  };
};

export default withSunnyPage()(observer(TrainSHome));
